<script setup>
import { pageTransition } from '~/helpers/animations/animations';
definePageMeta({
  pageTransition: pageTransition,
});

import { useMutation } from '@vue/apollo-composable';

const route = useRoute();
const gtm = useGtm();
const { locale, t } = useI18n();

const agreement = ref(false);

const showErrors = ref(false);
const loading = ref(false);
const success = ref(false);
const error = ref(false);

const pageQuery = gql`
  query GetPageData($routeId: String!) {
    page(route_id: $routeId) {
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      name
      content
    }
  }
`;

const { data } = await useAsyncQuery(pageQuery, { routeId: route.name });
const page = computed(() => data.value?.page);

const initialVariables = {
  listIds: [],
  email: '',
};

const variables = ref(initialVariables);

const formQuery = gql`
  mutation ($listIds: [ID]!, $email: String!) {
    newsletterSubscribe(listIds: $listIds, email: $email)
  }
`;

const { mutate: newsletterSubscribe } = useMutation(formQuery, { variables: variables.value });

const submit = () => {
  loading.value = true;
  try {
    useMutation(formQuery, { variables: variables.value });
    newsletterSubscribe(variables.value);
    loading.value = false;
    success.value = true;
    gtm.trackEvent({ event: 'newsletter_subscribe_success', url: window.location.href.replace(window.location.origin, '') });
  } catch (error) {
    console.log('error:', error);
    loading.value = false;
    gtm.trackEvent({ event: 'eventDetail_reservationErr', eventId: route.params.id, err: error, url: window.location.href.replace(window.location.origin, '') });
  }
};

const breadcrumbs = [{ title: t('pages.newsletter.breadcrumbs-title') }];

const gdprLink = ref({
  name: 'about-page',
  params: {
    slug: 'gdpr',
  },
});

const checkForm = () => {
  showErrors.value = true;
  formIsValid() ? submit() : gtm.trackEvent({ event: 'newsletter_subscribe_err', err: 'missing data', url: window.location.href.replace(window.location.origin, '') });
};

const formIsValid = () => {
  return variables.value.listIds.length && variables.value.email && mailIsValid() && agreement.value;
};

const mailIsValid = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(variables.value.email);
};

useHeadData({
  title: page.value?.seo_meta_title || page.value?.name || t('pages.newsletter.title').replace('&#8209;', '-'),
  description: page.value?.seo_meta_description || null,
  keywords: page.value?.seo_meta_keywords || null,
  image: page.value?.image || page.value?.cropped_detail_image || null,
  url: route.fullPath,
});
</script>

<template>
  <main class="main">
    <Breadcrumbs :items="breadcrumbs" />

    <div v-if="success" class="success-text mt-6">
      <img src="/img/ico-success.svg" alt="" draggable="false" />
      <p class="text fwm">{{ t('pages.newsletter.success') }}</p>
    </div>

    <template v-else>
      <h1 class="heading" v-html="t('pages.newsletter.title')" />
      <p class="text mt-5" v-html="t('pages.newsletter.description')"></p>

      <form class="form mt-5" @submit.prevent="checkForm()">
        <div>
          <div v-if="locale == 'cs'" class="no-outline">
            <div class="form__element">
              <label for="news">
                <input type="checkbox" class="checkbox" value="2" v-model="variables.listIds" id="news" />
                <span
                  >{{ t('pages.newsletter.option-2.name') }} – <i>{{ t('pages.newsletter.option-2.text') }}</i></span
                >
              </label>
            </div>
            <div class="form__element">
              <label for="kids">
                <input type="checkbox" class="checkbox" value="11" v-model="variables.listIds" id="kids" />
                <span
                  >{{ t('pages.newsletter.option-11.name') }} – <i>{{ t('pages.newsletter.option-11.text') }}</i></span
                >
              </label>
            </div>
            <div class="form__element">
              <input type="checkbox" class="checkbox" value="14" v-model="variables.listIds" id="tours" />
              <label for="tours">
                {{ t('pages.newsletter.option-14.name') }} – <i>{{ t('pages.newsletter.option-14.text') }}</i>
              </label>
            </div>
            <div class="form__element">
              <input type="checkbox" class="checkbox" value="9" v-model="variables.listIds" id="workshops" />
              <label for="workshops">
                {{ t('pages.newsletter.option-9.name') }} – <i>{{ t('pages.newsletter.option-9.text') }}</i>
              </label>
            </div>
            <div class="form__element">
              <input type="checkbox" class="checkbox" value="126" v-model="variables.listIds" id="schools" />
              <label for="schools">
                {{ t('pages.newsletter.option-126.name') }} – <i>{{ t('pages.newsletter.option-126.text') }}</i>
              </label>
            </div>
          </div>

          <div v-if="locale == 'en'" class="no-outline">
            <div class="form__element">
              <input type="checkbox" class="checkbox" :value="35" />
              <span>Programs in English</span>
            </div>
          </div>
        </div>

        <p v-if="showErrors && !variables.listIds.length" class="error">
          {{ t('pages.newsletter.validation.selected-required') }}
        </p>

        <div class="form__element mt-4">
          <div class="container-xs">
            <div class="form-input">
              <label for="email" class="form-input__label"></label>
              <div class="form-input__elem">
                <div class="form-input">
                  <label for="email" class="form-input__label">E-mail</label>
                  <div class="form-input__elem">
                    <input type="email" id="email" v-model.trim="variables.email" class="input input--full input--box" aria-label="email" />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="showErrors && !mailIsValid()" class="error">
              {{ t('pages.newsletter.validation.email-format') }}
            </div>

            <div v-if="showErrors && !variables.email" class="error">
              {{ t('pages.newsletter.validation.email-requirement') }}
            </div>
          </div>
        </div>
        <div class="form__element mt-3">
          <input type="checkbox" id="agreement" v-model="agreement" class="checkbox" />
          <label for="agreement">
            {{ t('pages.newsletter.agreement.text') }}
            <a :href="localePath(gdprLink)" class="link" target="_blank">
              {{ t('pages.newsletter.agreement.link') }}
            </a>
          </label>
        </div>

        <div v-if="showErrors && !agreement" class="error">
          {{ t('pages.newsletter.validation.terms-agreement') }}
        </div>

        <div class="form__element mt-5">
          <button type="submit" class="button" :disabled="loading">
            {{ t('pages.newsletter.subscribe') }}
          </button>
        </div>

        <div v-if="error" class="error">
          {{ t('pages.club.buy.submit-error') }}
        </div>
      </form>
    </template>

    <div class="container-content">
      <div class="mt-6 editor-paragraph">
        <p v-html="t('global.forms.agreement')" />
      </div>

      <template v-if="page">
        <DynamicPageContent v-if="page.content" :content="page.content" class="mt-5" />
      </template>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.success-text {
  display: flex;
  align-items: center;

  img {
    margin-right: 15px;
  }
}
.error {
  margin-top: 12px;
  color: #ef5858;
  transition-duration: 350ms;
}

input {
  margin-right: 8px;
}
</style>
